import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Dialog, DialogContent,
  IconButton, styled, Grid, FormControl, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import AddressService from './addressService';
import AlertService from "../../components/Alerts/alerts";
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import AddressPickerPopup from '../../components/AddressPicker/AddressPickerPopup';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    width: '100%',
    maxWidth: '600px',
    margin: 0,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
    },
  },
  backgroundColor: '#F0F5FB',
  '& .MuiInputLabel-root': {
    fontSize: '16px',
    color: '#757575',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: '13px',
  },
  marginBottom: theme.spacing(2),
}));


const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 'bold',
  boxShadow: 'none',
  width: '30%',
  backgroundColor: '#3488A4',
}));

const CancelButton = styled(Button)(({ theme }) => ({
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 'bold',
  color: '#16294E',
  boxShadow: 'none',
  width: '30%',
  borderRadius: '10px',
  backgroundColor: '#E7E7E7',
  border: '1px solid #E7E7E7',
}));
const UpdateAddressPopup = ({ open, onClose, address, onUpdateAddress }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    location: '',
    block: '',
    unit: '',
    label: '',
    latitude: '',
    longitude: '',
    address_url: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });

  useEffect(() => {
    if (address) {
      setFormData({
        ...address,
        location: address.location || `${address.block} ${address.unit}, ${address.city}, ${address.state} ${address.postalCode}, ${address.country}`.trim()
      });
    }
  }, [address]);

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [isGoogleAddressSelected, setIsGoogleAddressSelected] = useState(false);
  const [errors, setErrors] = useState({});

  const handleOpenPopup = () => setPopupOpen(true);
  const handleClosePopup = () => setPopupOpen(false);
  const handleSelectAddress = (address) => {
    //iph todo: 47
    //console.log("address", address);
    setSelectedAddress(address);
    setIsGoogleAddressSelected(true);
    setErrors({}); // Clear all errors when Google address is selected
    setFormData({
      location: address.location,
      block: address.block,
      unit: address.unit,
      label: address.label,
      latitude: address.latitude,
      longitude: address.longitude,
      address_url: `https://www.google.com/maps?q=${address.latitude},${address.longitude}`,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
    });
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    // Clear specific field error when user starts typing (only if not Google address)
    if (!isGoogleAddressSelected && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // If user manually changes any field, mark as not Google selected
    if (isGoogleAddressSelected) {
      setIsGoogleAddressSelected(false);
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // If Google address is selected, skip validation for auto-filled fields
    if (!isGoogleAddressSelected) {
      if (!formData.location || !formData.location.trim()) {
        newErrors.location = t('Please select an address from Google');
      }
      if (!formData.block || !formData.block.trim()) {
        newErrors.block = t('Block or Street name is required');
      }
      if (!formData.unit || !formData.unit.trim()) {
        newErrors.unit = t('Unit/House number is required');
      }
      if (!formData.city || !formData.city.trim()) {
        newErrors.city = t('City is required');
      }
      if (!formData.state || !formData.state.trim()) {
        newErrors.state = t('State/Province is required');
      }
      if (!formData.postalCode || !formData.postalCode.trim()) {
        newErrors.postalCode = t('Postal Code is required');
      }
      if (!formData.country || !formData.country.trim()) {
        newErrors.country = t('Country is required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateAddress = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        address_id: address._id,
        label: '',
        unit: formData.unit,
        location: formData.location,
        block: formData.block,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country,
        latitude: formData.latitude,
        longitude: formData.longitude
      };
      const response = await AddressService.updateAddress(data);

      if (response.data.status) {

        AlertService.swalSuccessAlert(t("Address updated successfully."));
        onUpdateAddress(response.data.address);
        onClose();
      } else {
        AlertService.swalErrorAlert(response.data.message?.[0]?.message || response.data.message || t("Error updating address."));
      }
    } catch (error) {
      AlertService.swalErrorAlert(t("Error updating address. Please try again."));
    }
  };

  const malaysiaStates = [
    "Johor",
    "Kedah",
    "Kelantan",
    "Kuala Lumpur",
    "Labuan",
    "Melaka",
    "Negeri Sembilan",
    "Pahang",
    "Penang",
    "Perak",
    "Perlis",
    "Putrajaya",
    "Sabah",
    "Sarawak",
    "Selangor",
    "Terengganu"
  ];

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth>
      <Header>
        <IconButton onClick={onClose}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">{t('Address')}</Typography>
        <Box width={24} />
      </Header>
      <DialogContent sx={{ padding: 5 }}>
        <SearchContainer>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }} variant="h6">{t('Address')}</Typography>
          <StyledTextField
            label="Selected Address"
            value={selectedAddress.location || formData.location || ''}
            fullWidth
            onClick={handleOpenPopup}
            readOnly
            error={!!errors.location}
            helperText={errors.location}
          />
          <AddressPickerPopup
            open={popupOpen}
            onClose={handleClosePopup}
            onSelect={handleSelectAddress}
          />
          {/* <StyledTextField
            key={open ? 'open' : 'closed'}
            inputRef={inputRef}
            margin="normal"
            fullWidth
            placeholder="Search Google Location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <LocationOnIcon color="action" />,
            }}
            sx={{
              '& .MuiInputBase-root': {
                zIndex: 2,
              }
            }}
          /> */}
        </SearchContainer>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('Block or Street name')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("Block or Street name")}
              name="block"
              value={formData.block}
              onChange={handleInputChange}
              error={!!errors.block}
              helperText={errors.block}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('Unit/House no.')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("Unit/House no.")}
              name="unit"
              value={formData.unit}
              onChange={handleInputChange}
              error={!!errors.unit}
              helperText={errors.unit}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('City')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("City")}
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              error={!!errors.city}
              helperText={errors.city}
            />
          </Grid>
          {/* <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('State/Province')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("State/Province")}
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              error={!!errors.state}
              helperText={errors.state}
            />
          </Grid> */}

          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">
              {t('State/Province')}
            </Typography>

            <StyledTextField
              select
              fullWidth
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              error={!!errors.state}
              helperText={errors.state}
            >
              <MenuItem value="">{t("Select State")}</MenuItem>
              {malaysiaStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </StyledTextField>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('Postal Code')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("Postal Code")}
              name="postalCode"
              value={formData.postalCode}
              onChange={handleInputChange}
              error={!!errors.postalCode}
              helperText={errors.postalCode}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }} variant="h6">{t('Country')}</Typography>
            <StyledTextField
              fullWidth
              placeholder={t("Country")}
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              error={!!errors.country}
              helperText={errors.country}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <CancelButton
            fullWidth
            variant="outlined"
            onClick={onClose}
            startIcon={<CloseIcon />}
          >
            {t("Cancel")}
          </CancelButton>
          <ActionButton
            fullWidth
            variant="contained"
            onClick={handleUpdateAddress}
            startIcon={<CheckIcon />}
          >
            {t("Update")}
          </ActionButton>
        </Box>
      </DialogContent>
      {/* z-index: 1300 !important; */}

      <style>{`
                 .pac-container {
        z-index: 9999 !important;
        position: absolute !important;
    }
            `}</style>
    </StyledDialog>
  );
};

export default UpdateAddressPopup;